/**
 * Workspace entry — which workspace this session is in, decided before the shell renders
 * (PAGES.md "Workspace entry"; behaviour reference: frontend/src/components/WorkspaceEntryGate.tsx).
 *
 *   1. A NAMED workspace (its link /t/<id>, or the workspace-ID box) is entered first. A refusal
 *      shows one generic message whatever the reason — it never says whether the workspace exists.
 *   2. Someone whose workspaces all require a named sign-in, in a session that named none, is
 *      told to use the workspace's link (403 tenant_entry_required).
 *   3. Someone with more than one workspace picks one, once per browser session, last used on top.
 *
 * The picker and notice are the shared @inferrex/ui ones; this file only sequences them.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  WorkspacePicker, WorkspaceEntryNotice, takePendingTenantEntry, shouldShowWorkspacePicker,
  readWorkspaceLastUsed, recordWorkspaceChoice, workspaceChosenThisSession, WORKSPACE_SIGN_IN_PATH,
  StateBlock,
} from '@inferrex/ui';
import { enterTenant, useMyWorkspaces, useSwitchWorkspace, TENANT_ENTRY_REQUIRED } from '@inferrex/api-client';

const GENERIC_REFUSAL = 'We can’t sign you in to that workspace. Check the workspace ID or link, or ask its administrator to add you.';

type Phase = { kind: 'entering' } | { kind: 'refused'; message: string } | { kind: 'ready' };

function FullScreenLoading() {
  return <div className="app-spinner"><StateBlock kind="loading" rows={2} message="Opening your workspace…" /></div>;
}

export function WorkspaceEntry({ children }: { children: ReactNode }) {
  const [pending] = useState(() => takePendingTenantEntry());
  const [phase, setPhase] = useState<Phase>(() => (pending ? { kind: 'entering' } : { kind: 'ready' }));
  const [chosen, setChosen] = useState(() => workspaceChosenThisSession());
  const workspaces = useMyWorkspaces();
  const switchWorkspace = useSwitchWorkspace();

  useEffect(() => {
    if (!pending) return;
    if (pending === 'invalid') {
      setPhase({ kind: 'refused', message: GENERIC_REFUSAL });
      return;
    }
    let cancelled = false;
    enterTenant(pending)
      .then((entry) => {
        if (cancelled) return;
        recordWorkspaceChoice(entry.tenantId);
        setChosen(true);
        setPhase({ kind: 'ready' });
      })
      .catch((err: unknown) => {
        if (!cancelled) setPhase({ kind: 'refused', message: err instanceof Error ? err.message : GENERIC_REFUSAL });
      });
    return () => { cancelled = true; };
  }, [pending]);

  if (phase.kind === 'entering') return <FullScreenLoading />;
  if (phase.kind === 'refused') {
    return (
      <WorkspaceEntryNotice
        title="Can’t sign in to that workspace"
        message={phase.message}
        primaryLabel="Continue to my workspaces"
        onPrimary={() => setPhase({ kind: 'ready' })}
        secondaryLabel="Try another workspace ID"
        onSecondary={() => window.location.assign(WORKSPACE_SIGN_IN_PATH)}
      />
    );
  }

  const code = (workspaces.error as { code?: string } | null)?.code;
  if (code === TENANT_ENTRY_REQUIRED) {
    return (
      <WorkspaceEntryNotice
        title="Use your workspace’s sign-in link"
        message="Your workspace requires you to sign in with its link or its workspace ID. Ask its administrator if you do not have them."
        primaryLabel="Enter a workspace ID"
        onPrimary={() => window.location.assign(WORKSPACE_SIGN_IN_PATH)}
      />
    );
  }
  if (workspaces.isLoading) return <FullScreenLoading />;

  const list = workspaces.data ?? [];
  if (shouldShowWorkspacePicker({ workspaceCount: list.length, chosenThisSession: chosen, enteringNamedTenant: false })) {
    const lastUsed = readWorkspaceLastUsed();
    return (
      <WorkspacePicker
        options={list.map((w) => ({ id: w.id, name: w.name, role: w.role, lastUsedAt: lastUsed[w.id] ?? null }))}
        onSelect={(id) => {
          recordWorkspaceChoice(id);
          switchWorkspace(id);
          setChosen(true);
        }}
        onUseWorkspaceId={() => window.location.assign(WORKSPACE_SIGN_IN_PATH)}
      />
    );
  }

  return <>{children}</>;
}
